import imageCompression from 'browser-image-compression';
import SyiMetrics from '@gumtree/web-bff/src/metrics/modules/syi-metrics';
import Sortable from 'sortablejs';
import * as Sentry from '@sentry/browser';

import {
    getPageStateThunk,
    PlaceholderImageDef,
    SyiImageDef,
    updateSyi,
    updateSyiFormError,
} from '../reducers/syi';
import { dtlSyi, fetchOrThrow } from './dtl-syi';
import { trackEvents } from './tracking';
import { AppViewDevices } from '@gumtree/shared/src/constants/app-view';
import { trackV2 } from './common-utils';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { computeListingDetails } from './syi-model';

export default class ImageUpload {
    get el() {
        return dtlSyi.form!.el.querySelector('.panel.images ul.images') as HTMLUListElement;
    }
    get mainHiddenInput() {
        return dtlSyi.form!.el.querySelector(
            '.panel.images input[name=mainImageId]'
        ) as HTMLInputElement;
    }
    /** Possibly null if cannot add image */
    get fileInput() {
        return dtlSyi.form!.el.querySelector('.panel.images input[type=file]') as
            | null
            | (HTMLInputElement & {
                  type: 'file';
              });
    }
    get url() {
        return `${dtlSyi.postadURL}/images`;
    }
    get deleteImageUrl() {
        return `${dtlSyi.postadURL}/images/delete`;
    }
    get orderImageUrl() {
        return `${dtlSyi.postadURL}/images/order`;
    }

    options = {
        maximages: 20,
        downsize: {
            compressor: {
                maxWidthOrHeight: 1600,
                initialQuality: 0.8,
            },
        },
    };
    multiUpload = false;
    QUEUE_STATUS = 'IDLE' as QueueStatus;
    uploadQueue = [] as QueueItem[];
    sortable!: Sortable;

    addIdToPlaceholderDef({ id, url }, item: QueueItem) {
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        const found = imageDefs.find((def) => 'file' in def && def.file === item.formQuery.image);
        if (found) {
            found.id = id;
            found.url = url;
        }
    }

    addToQueue(data: QueueItem) {
        this.uploadQueue.push(data);
        this.checkQueueStatus();
    }

    checkQueueStatus() {
        if (this.uploadQueue.length > 0 && this.QUEUE_STATUS === 'IDLE') {
            const firstItem = this.uploadQueue.shift();
            this.changeQueueStatus('BUSY');
            firstItem && this.uploadXhr(firstItem);
        }
        return this.QUEUE_STATUS;
    }
    changeQueueStatus(status: QueueStatus) {
        this.QUEUE_STATUS = status;
        this.checkQueueStatus();
    }

    deleteImageFail() {
        this.printError("Sorry, we couldn't delete your image, try again");
    }

    enableSortable(state: boolean) {
        this.sortable.option('disabled', !state);
    }

    async deleteImg(item: SyiImageDef) {
        this.imageXhr({ id: item.id }, 'POST', 'delete')
            .then((resp) => this.deleteImageSuccess(resp as { id: number }))
            .catch(() => this.deleteImageFail());
    }

    deleteImageSuccess(response: { id: number }) {
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        const found = imageDefs.find((def) => def.id === response.id);

        if (found) {
            // Fade then remove
            found.fade = true;
            dtlSyi.dispatch(updateSyi({ imageDefs: imageDefs.slice() }));
            setTimeout(() => {
                const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
                dtlSyi.dispatch(
                    updateSyi({ imageDefs: imageDefs.filter((def) => def.id !== response.id) })
                );
            }, 300);

            trackV2('ImageUploadDelete');
        }
    }

    async fileUpload(files: File[]) {
        const isImageLimitReached = this.isImageLimitReached(files.length);
        this.multiUpload = files.length > 1;

        //Clear previous errors when upload new images
        dtlSyi.dispatch(updateSyiFormError({ elName: 'imageIds[]', errors: [] }));

        //Limit number of images
        if (isImageLimitReached) {
            files = files.slice(0, this.options.maximages - this.shownImageNumber());
        }

        this.enableSortable(files.length === 0);

        let placeholderPosition = this.shownImageNumber() + 1;

        const { appViewDevice } = dtlSyi.dispatch(getPageStateThunk()).page;

        for (const file of files) {
            let resizedBlob = undefined as undefined | Blob;

            if (appViewDevice === AppViewDevices.IOS) {
                Sentry.captureMessage(
                    `Attempting to resize ${file.type} with a size of ${file.size} and a name of ${file.name} by browser-image-compression`
                );
            }

            if (this.isImageFile(file)) {
                const startTime = Date.now();
                await imageCompression(file, this.options.downsize.compressor)
                    .then((result) => (resizedBlob = result))
                    .catch((err) => {
                        /** Fallback to no browser-side resize */
                        const extraLoggingInfo = file
                            ? `type: ${file.type}. size: ${file.size}. name: ${
                                  file.name
                              }.  error: ${err.toString()}`
                            : `error: ${err.toString()}`;

                        Sentry.captureException(
                            `Error resizing image.  Falling back to original. ${extraLoggingInfo}`,
                            err
                        );
                    })
                    .finally(() => {
                        const newImageDef: PlaceholderImageDef = {
                            file,
                            blobUrl: URL.createObjectURL(resizedBlob || file),
                            waiting: true,
                            startTime,
                        };
                        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
                        dtlSyi.dispatch(
                            updateSyi({
                                imageDefs: imageDefs.concat(newImageDef),
                            })
                        );
                        this.startUpload({
                            formQuery: { image: file, resizedBlob, position: placeholderPosition },
                        });
                    });
            } else {
                this.printError(`Image type not supported - please use jpeg, jpg or png`);
                this.changeQueueStatus('IDLE');
            }

            placeholderPosition++;
        }
    }

    getDomImageIds() {
        return Array.from(this.el.querySelectorAll('input[name="imageIds[]"]')).map((el) =>
            Number((el as HTMLInputElement).value)
        );
    }

    /**
     * Used for re-ordering images or deleting images.
     */
    async imageXhr(ajaxData: any, method: 'POST', endpointType: 'delete' | 'reorder') {
        const stringifiedAjaxData = JSON.stringify(ajaxData);

        if (endpointType === 'delete') {
            return (await fetchOrThrow(
                this.deleteImageUrl,
                {
                    method,
                    headers: { 'content-type': 'application/json' },
                    body: stringifiedAjaxData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id/images/delete',
                    method: 'POST',
                }
            ).then((x) => x.json())) as { id: number };
        }
        if (endpointType === 'reorder') {
            await fetchOrThrow(
                this.orderImageUrl,
                {
                    method,
                    headers: { 'content-type': 'application/json' },
                    body: stringifiedAjaxData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id/images/order',
                    method: 'POST',
                }
            );
        }
        return undefined;
    }

    imageUploadError(_item: QueueItem, error: any) {
        const categoryId = dtlSyi.model.data.categoryId!;
        const { appViewDevice } = dtlSyi.dispatch(getPageStateThunk()).page;

        SyiMetrics.registerImageUploadEvent('fail', categoryId, appViewDevice);

        trackEvents('AddImageFail');
        this.printError(`${error}`);
        this.changeQueueStatus('IDLE');
    }

    imageUploadResponseHandler(item: QueueItem, resp: any) {
        // 🚧 ?
        // var response = JSON.parse(resp.replace(
        //     /^\s*<pre[^>]*>/, ''
        // ).replace(
        //     /<\/pre>\s*$/, ''));
        this.imageUploadSuccess(item, resp);
    }

    imageUploadSuccess(item: QueueItem, resp: any) {
        const categoryId = dtlSyi.model.data.categoryId!;
        const { appViewDevice } = dtlSyi.dispatch(getPageStateThunk()).page;

        SyiMetrics.registerImageUploadEvent('success', categoryId, appViewDevice);

        trackEvents('AddImageSuccess');
        this.addIdToPlaceholderDef({ id: resp.id, url: resp.url }, item);
        this.stopSpinner(item);

        if (!this.multiUpload) {
            this.changeQueueStatus('IDLE');
        }

        this.enableSortable(this.checkQueueStatus() === 'IDLE');
    }

    init() {
        const { device } = dtlSyi.dispatch(getPageStateThunk()).baseConfig;

        this.uploadQueue = [];
        const cleanup = this.initFileInput();
        this.initSortable();

        if (device.type === 'desktop') {
            this.initDragAndDrop();
        }

        return cleanup;
    }

    initFileInput() {
        if (this.fileInput) {
            const onChange = async () => {
                if (this.fileInput?.files) {
                    await this.fileUpload(Array.from(this.fileInput.files));
                    trackEvents('AddImageBegin');
                }
            };
            this.fileInput.addEventListener('change', onChange);
            return () => this.fileInput?.removeEventListener('change', onChange);
        } else {
            return undefined;
        }
    }

    initDragAndDrop() {
        const dropArea = document.querySelector('.add-images') as HTMLDivElement;

        ['dragenter', 'dragleave', 'dragover', 'drop'].forEach((eventName) => {
            dropArea.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
        });

        ['dragenter', 'dragover'].forEach((eventName) => {
            dropArea.addEventListener(eventName, () =>
                dtlSyi.dispatch(updateSyi({ isDragging: true }))
            );
        });

        ['dragleave', 'drop'].forEach((eventName) => {
            dropArea.addEventListener(eventName, () =>
                dtlSyi.dispatch(updateSyi({ isDragging: false }))
            );
        });

        dropArea.addEventListener('drop', (e) => this.onFileDrop(e));
    }

    onFileDrop(e) {
        const dt = e.dataTransfer;
        const files: FileList = dt.files;
        const imageFiles: File[] = [...files].filter((file) => this.isImageFile(file));
        this.fileUpload([...imageFiles]);
    }

    isImageFile(file: File) {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', '.jpeg', '.jpg', '.png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        return (
            allowedTypes.includes(file.type) ||
            (fileExtension && allowedTypes.some((type) => type.includes(fileExtension)))
        );
    }

    initSortable() {
        this.sortable = new Sortable(this.el, {
            sort: true,
            animation: 200,
            delay: 300,
            delayOnTouchOnly: true,
            draggable: '.js-sortable',
            forceFallback: true,
            onEnd: () => this.onDropSortable(),
        });
    }

    isImageLimitReached(offset: number) {
        return this.shownImageNumber() + (offset || 0) >= this.options.maximages;
    }

    onDropSortable() {
        // Must get current ordering from the DOM
        const imageIds = this.getDomImageIds();

        this.updateMainImgId();

        this.imageXhr(
            {
                imgIds: imageIds,
            },
            'POST',
            'reorder'
        );

        trackV2('ImageUploadReorder');
    }

    printError(errorMessage: string) {
        // 🚧 originally this error was appended
        dtlSyi.dispatch(
            updateSyiFormError({ elName: 'imageIds[]', errors: [errorMessage], shouldShow: true })
        );
    }

    shownImageNumber() {
        const lis = Array.from(this.el.children);
        // Possibly one additional <li> (li.add-image)
        return lis.filter((el) => el.classList.contains('js-sortable')).length;
    }

    startUpload(item: QueueItem) {
        if (this.multiUpload) {
            this.uploadXhr(item);
        } else {
            this.addToQueue(item);
        }
    }

    trackEvents(startTime?: number) {
        if (!startTime) {
            return;
        }
        const duration = Date.now() - startTime;
        trackGA4Event<GA4.AddImageSuccess>({
            event: 'add_image_success',
            formName: 'syi-form',
            duration,
            accountId: dtlSyi.accountId,
            ...computeListingDetails(),
        });
    }

    stopSpinner(item: QueueItem) {
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        dtlSyi.dispatch(
            updateSyi({
                imageDefs: imageDefs.map((def) => {
                    if ('file' in def && def.file === item.formQuery.image) {
                        def.waiting = false;
                        this.trackEvents(def.startTime);
                    }
                    return def;
                }),
            })
        );
    }

    updateMainImgId() {
        const mainImgId = dtlSyi.dispatch(getPageStateThunk()).syi.imageDefs[0]?.id ?? 0;
        this.mainHiddenInput.setAttribute('value', `${mainImgId}`);
    }

    async uploadXhr(item: QueueItem) {
        const formData = new FormData();
        formData.append('image', item.formQuery.resizedBlob || item.formQuery.image);
        formData.append('position', `${item.formQuery.position}`);

        try {
            const resp = await fetchOrThrow(
                this.url,
                {
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id/images',
                    method: 'POST',
                }
            ).then((x) => x.json());

            this.imageUploadResponseHandler(item, resp);
        } catch (e) {
            this.imageUploadError(item, e);
        }
    }
}

interface QueueItem {
    formQuery: { image: File; resizedBlob?: Blob; position: number };
}

type QueueStatus = 'BUSY' | 'IDLE';
